import type { ClientOrganizationProfileRoleEnum } from '@DigitaleDoerfer/digitale-doerfer-api';
import { ClientExceptionEntityTypeEnum } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu } from 'react-native-paper';

import { getClientErrorType } from '@/features/api/hooks/errorMiddleware';
import type { ClientOrganizationMember } from '@/features/api/hooks/grapevine-organization/useGetOrganizationMembers';
import { useRemoveOrganizationMember } from '@/features/api/hooks/grapevine-organization/useRemoveOrganizationMember';
import { useAppState } from '@/features/authorization/provider/useAppState';
import { usePopup } from '@/features/popup/provider/usePopup';
import { ChangeRoleModal } from '@/features/profile-setting/components/ChangeRoleModal';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import type { ExtendedMenuProps } from '@/shared/components/ExtendedMenu';
import { ExtendedMenu } from '@/shared/components/ExtendedMenu';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

type Props = {
  organizationId: string;
  member: ClientOrganizationMember;
  currentRoleKey: ClientOrganizationProfileRoleEnum;
  /**
   * True when the member is the only admin of this organization. Blocks leave
   * and remove up-front, and propagates to {@link ChangeRoleModal} which uses
   * it to block a demotion. The backend `LAST_ADMIN_CANNOT_BE_REMOVED` handler
   * stays as a race-condition safety net (a second admin could be demoted
   * between fetch and click).
   */
  isLastAdmin: boolean;
  /**
   * True when the current user is an admin of this org. Gates admin-only
   * actions (change role, remove, private-message another member). When false
   * the menu only renders "Organisation verlassen" — the row's caller already
   * hides the menu entirely for non-self rows in that case.
   */
  currentUserIsAdmin: boolean;
} & Omit<ExtendedMenuProps, 'children'>;

/**
 * Row-level "..." menu for a member on the organization members screen.
 * - Own row: "Rolle ändern" + "Organisation verlassen"
 * - Other row: "Rolle ändern" + "Privat schreiben" + "Aus Organisation entfernen"
 * "Rolle ändern" opens {@link ChangeRoleModal}; remove/leave uses a plain confirmation alert.
 */
export const MemberActionMenu: FC<Props> = ({
  organizationId,
  member,
  currentRoleKey,
  isLastAdmin,
  currentUserIsAdmin,
  onDismiss,
  ...menuProps
}) => {
  const theme = useAppTheme();
  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.organization-members'
  });
  const { showAlert } = usePopup();
  const { userId } = useAppState();

  const isSelf = userId === member.id;

  const { mutate: removeMember } = useRemoveOrganizationMember();

  const [isChangeRoleVisible, setIsChangeRoleVisible] = useState(false);

  const displayName = useMemo(
    () => [member.firstName, member.lastName].filter(Boolean).join(' '),
    [member.firstName, member.lastName]
  );

  const showLastAdminBlockAlert = useCallback(
    (action: 'leave' | 'remove') => {
      const labels =
        action === 'leave'
          ? t('alerts.cannotLeaveLastAdmin', { returnObjects: true })
          : t('alerts.cannotRemoveLastAdmin', { returnObjects: true });
      showAlert({ dismissable: true, labels });
    },
    [showAlert, t]
  );

  const showGenericFailureAlert = useCallback(() => {
    showAlert({
      dismissable: true,
      labels: t('alerts.actionFailed', { returnObjects: true })
    });
  }, [showAlert, t]);

  // Kept as a race-condition safety net — pre-flight covers the common case.
  const handleRemoveError = useCallback(
    (error: unknown, action: 'leave' | 'remove') => {
      const type = getClientErrorType(error);
      if (type === ClientExceptionEntityTypeEnum.LastAdminCannotBeRemoved) {
        showLastAdminBlockAlert(action);
      } else {
        showGenericFailureAlert();
      }
    },
    [showGenericFailureAlert, showLastAdminBlockAlert]
  );

  const handleChangeRole = useCallback(() => {
    onDismiss?.();
    setIsChangeRoleVisible(true);
  }, [onDismiss]);

  const handleRemove = useCallback(() => {
    onDismiss?.();
    const action = isSelf ? 'leave' : 'remove';
    if (isLastAdmin) {
      showLastAdminBlockAlert(action);
      return;
    }
    const labels = t(
      isSelf ? 'alerts.leaveOrganization' : 'alerts.removeMember',
      {
        returnObjects: true,
        name: displayName
      }
    );
    showAlert({
      dismissable: true,
      confirmColor: theme.colors.error,
      labels,
      onConfirm: () =>
        removeMember(
          {
            organizationId,
            roleAssignmentId: member.roleAssignmentId
          },
          { onError: (error) => handleRemoveError(error, action) }
        )
    });
  }, [
    displayName,
    handleRemoveError,
    isLastAdmin,
    isSelf,
    member.roleAssignmentId,
    onDismiss,
    organizationId,
    removeMember,
    showAlert,
    showLastAdminBlockAlert,
    t,
    theme.colors.error
  ]);

  const handleWritePrivate = useCallback(() => {
    // Todo: wire private-chat navigation once a person-scoped NewChat route exists
    onDismiss?.();
  }, [onDismiss]);

  return (
    <>
      <ExtendedMenu onDismiss={onDismiss} {...menuProps}>
        {currentUserIsAdmin && (
          <Menu.Item title={t('row.changeRole')} onPress={handleChangeRole} />
        )}
        {currentUserIsAdmin && !isSelf && (
          <Menu.Item
            title={t('row.writePrivate')}
            onPress={handleWritePrivate}
          />
        )}
        <Menu.Item
          title={t(isSelf ? 'row.leaveOrg' : 'row.removeFromOrg')}
          onPress={handleRemove}
          theme={{ colors: { onSurface: theme.colors.error } }}
        />
      </ExtendedMenu>
      {currentUserIsAdmin && (
        <ChangeRoleModal
          organizationId={organizationId}
          member={member}
          currentRoleKey={currentRoleKey}
          isLastAdmin={isLastAdmin}
          isVisible={isChangeRoleVisible}
          onClose={() => setIsChangeRoleVisible(false)}
        />
      )}
    </>
  );
};
